import React, { useMemo } from 'react';
import { Alert, Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';


const COLORS = ['#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#0891b2', '#dc2626'];

function label(value) {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function number(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function date(value) {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

export default function RunTelemetryPanel({ telemetry }) {
  const rows = useMemo(() => (telemetry?.rows || []).map((row) => ({ ...row, numericValue: number(row.value) })), [telemetry]);
  const fields = useMemo(() => [...new Set(rows.map((row) => row.field).filter(Boolean))].sort(), [rows]);
  const numericFields = useMemo(() => [...new Set(rows.filter((row) => row.numericValue !== null).map((row) => row.field))].sort(), [rows]);
  const chartData = useMemo(() => {
    const byTime = new Map();
    rows.filter((row) => row.numericValue !== null).forEach((row) => {
      const point = byTime.get(row.time) || { time: row.time };
      point[row.field] = row.numericValue;
      byTime.set(row.time, point);
    });
    return [...byTime.values()].sort((left, right) => new Date(left.time) - new Date(right.time));
  }, [rows]);
  const latest = useMemo(() => {
    const values = new Map();
    rows.forEach((row) => values.set(row.field, row));
    return values;
  }, [rows]);

  if (!telemetry?.configured) {
    return <Alert severity="info">{telemetry?.message || 'Telemetry is not configured.'}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Alert severity={telemetry?.fallback_used ? 'info' : 'success'}>
        {telemetry?.match_mode === 'run_id'
          ? 'Showing telemetry explicitly tagged with this Run ID.'
          : 'No Run-tagged samples were found; showing the Asset telemetry within the Run time range.'}
      </Alert>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`${rows.length} samples`} />
        <Chip size="small" label={`${numericFields.length} numeric fields`} variant="outlined" />
        {telemetry?.live ? <Chip size="small" label="Live" color="success" /> : null}
      </Stack>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Telemetry Trends</Typography>
        {chartData.length && numericFields.length ? (
          <Box sx={{ width: '100%', height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 12, right: 24, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="time" tickFormatter={(value) => new Date(value).toLocaleTimeString()} minTickGap={28} />
                <YAxis width={56} />
                <Tooltip labelFormatter={date} />
                <Legend formatter={label} />
                {numericFields.slice(0, 6).map((field, index) => (
                  <Area key={field} type="monotone" dataKey={field} stroke={COLORS[index % COLORS.length]} fill={COLORS[index % COLORS.length]} fillOpacity={0.08} connectNulls isAnimationActive={false} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">No numeric telemetry is available for this Run.</Typography>
        )}
      </Paper>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Field</TableCell><TableCell>Latest Value</TableCell><TableCell>Time</TableCell></TableRow></TableHead>
          <TableBody>
            {fields.map((field) => (
              <TableRow key={field}>
                <TableCell sx={{ fontWeight: 750 }}>{label(field)}</TableCell>
                <TableCell>{String(latest.get(field)?.value ?? 'N/A')}</TableCell>
                <TableCell>{date(latest.get(field)?.time)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}
